import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";

const MODULO = "Actividades";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const obtenerFechaActual = () => {
  const hoy = new Date();
  const year = hoy.getFullYear();
  const month = String(hoy.getMonth() + 1).padStart(2, "0");
  const day = String(hoy.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const RegistrarActividad = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const esEditar = location.pathname.includes("/actividad/editar");

  const [nombreActividad, setNombreActividad] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [fecha, setFecha] = useState(obtenerFechaActual());
  const [matricula, setMatricula] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const disabledAll = useMemo(() => loading || saving, [loading, saving]);

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };
  };

  const getModuloActividades = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";
          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloActividades = useMemo(() => getModuloActividades(), []);

  const puedeCrear =
    moduloActividades?.crear === true ||
    moduloActividades?.puedeCrear === true ||
    moduloActividades?.create === true;

  const puedeActualizar =
    moduloActividades?.actualizar === true ||
    moduloActividades?.editar === true ||
    moduloActividades?.puedeActualizar === true ||
    moduloActividades?.update === true;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/login");
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const edit = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/actividad/listid/${id}`,
        authConfig()
      );

      setNombreActividad(data?.nombreActividad || "");
      setDescripcion(data?.descripcion || "");
      setFecha(data?.fecha ? data.fecha.substring(0, 10) : obtenerFechaActual());
      setMatricula(data?.matricula || "");
    } catch (error) {
      console.error("Error al cargar actividad:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire("Error", "No se pudo cargar la actividad.", "error");
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion]);

  useEffect(() => {
    if (esEditar) {
      edit();
    } else {
      setMatricula(id || "");
      setFecha(obtenerFechaActual());
    }
  }, [esEditar, edit, id]);

  const validarFormulario = () => {
    if (!nombreActividad.trim()) {
      Swal.fire(
        "Campo requerido",
        "Ingrese el nombre de la actividad.",
        "warning"
      );
      return false;
    }

    if (!descripcion.trim()) {
      Swal.fire("Campo requerido", "Ingrese la descripción.", "warning");
      return false;
    }

    if (!fecha) {
      Swal.fire("Campo requerido", "No se encontró la fecha actual.", "warning");
      return false;
    }

    if (!esEditar && !matricula) {
      Swal.fire("Error", "No se encontró la matrícula relacionada.", "error");
      return false;
    }

    return true;
  };

  const save = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Acceso denegado",
        "No tienes permisos para crear actividades.",
        "warning"
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        nombreActividad,
        descripcion,
        fecha: obtenerFechaActual(),
      };

      await axios.post(
        `/api/actividad/add/${matricula}`,
        payload,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "Actividad registrada",
        text: "La actividad se guardó correctamente.",
        timer: 1600,
        showConfirmButton: false,
      });

      navigate(-1);
    } catch (error) {
      console.error("Error al guardar actividad:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "No se pudo registrar la actividad.",
      });
    } finally {
      setSaving(false);
    }
  };

  const update = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Acceso denegado",
        "No tienes permisos para actualizar actividades.",
        "warning"
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        nombreActividad,
        descripcion,
      };

      const { data } = await axios.put(
        `/api/actividad/update/${id}`,
        payload,
        authConfig()
      );

      await Swal.fire({
        icon: "success",
        title: "Actividad actualizada",
        text: data?.mensaje || "La actividad se actualizó correctamente.",
        timer: 1600,
        showConfirmButton: false,
      });

      navigate(-1);
    } catch (error) {
      console.error("Error al actualizar actividad:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire({
        icon: "error",
        title: "Error al actualizar",
        text:
          error.response?.data?.detalle ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          "No se pudo actualizar la actividad.",
      });
    } finally {
      setSaving(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) return;

    if (esEditar) {
      await update();
    } else {
      await save();
    }
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {esEditar ? "Editar Actividad" : "Registrar Actividad"}
                </h5>

                <small className="text-secondary">
                  {esEditar
                    ? "Modifica la información permitida de la actividad."
                    : "Registra una nueva actividad con la fecha actual."}
                </small>
              </div>

              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={() => navigate(-1)}
                disabled={disabledAll}
              >
                <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                Volver
              </button>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading ? (
              <div className="text-center text-secondary py-5">
                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>
                <div className="mt-3">Cargando actividad…</div>
              </div>
            ) : (
              <form onSubmit={actions}>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">
                      Nombre de la actividad
                    </label>

                    <input
                      type="text"
                      className="form-control"
                      value={nombreActividad}
                      onChange={(e) => setNombreActividad(e.target.value)}
                      placeholder="Ej: Clase de programación"
                      disabled={disabledAll}
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-semibold">
                      Fecha actual
                    </label>

                    <input
                      type="date"
                      className="form-control bg-body-tertiary"
                      value={fecha}
                      readOnly
                      disabled
                    />

                    <small className="text-secondary">
                      La fecha se asigna automáticamente y no puede modificarse.
                    </small>
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-semibold">
                      Descripción
                    </label>

                    <textarea
                      className="form-control"
                      rows="4"
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      placeholder="Describe la actividad realizada..."
                      disabled={disabledAll}
                    />
                  </div>

                  <div className="col-12">
                    <div className="border rounded-4 p-3 bg-body-tertiary">
                      <small className="text-secondary">
                        {esEditar
                          ? "Actividad en edición"
                          : "Matrícula relacionada"}
                      </small>

                      <div className="fw-semibold text-dark">
                        {esEditar ? id : matricula || "No disponible"}
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="d-flex justify-content-end gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => navigate(-1)}
                        disabled={disabledAll}
                      >
                        <i className="fa-solid fa-xmark me-2"></i>
                        Cancelar
                      </button>

                      <button
                        type="submit"
                        className="btn btn-dark"
                        disabled={disabledAll}
                      >
                        <i
                          className={`fa-solid ${
                            saving
                              ? "fa-spinner fa-spin"
                              : esEditar
                              ? "fa-rotate"
                              : "fa-floppy-disk"
                          } me-2`}
                        ></i>

                        {esEditar
                          ? "Actualizar Actividad"
                          : "Guardar Actividad"}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarActividad;